import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { employeesAPI } from 'api/employees'
import type { Employee } from 'Types/Employee'
import { branchChanged } from './branchSlice'

interface EmployeesState {
 data: Employee[]
 loading: boolean
 error: string | null
 pagination: {
 total: number
 per_page: number
 current_page: number
 last_page: number
 to: number
 } | null
 currentEmployee: Employee | null
 currentLoading: boolean
}

const initialState: EmployeesState = {
 data: [],
 loading: false,
 error: null,
 pagination: null,
 currentEmployee: null,
 currentLoading: false,
}

export const fetchEmployees = createAsyncThunk(
 'employees/fetchEmployees',
 async (params: Record<string, unknown> = {}, { rejectWithValue }) => {
 try {
 const response = await employeesAPI.getAll(params as any)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.employees.fetchFailed'))
 }
 }
)

export const fetchEmployeeById = createAsyncThunk(
 'employees/fetchEmployeeById',
 async (id: number, { rejectWithValue }) => {
 try {
 const response = await employeesAPI.getById(id)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.employees.fetchOneFailed'))
 }
 }
)

export const createEmployee = createAsyncThunk(
 'employees/createEmployee',
 async (data: any, { rejectWithValue }) => {
 try {
 const response = await employeesAPI.create(data)
 return response
 } catch (error: any) {
 const msg = error.response?.data?.message
 const errors = error.response?.data?.errors
 const errData = error.response?.data?.data
 if (errors) {
 const firstError = Object.values(errors)[0]
 return rejectWithValue(Array.isArray(firstError) ? firstError[0] : firstError)
 }
 if (errData) {
 const firstError = Object.values(errData)[0]
 return rejectWithValue(Array.isArray(firstError) ? firstError[0] : firstError)
 }
 return rejectWithValue(msg || i18n.t('errors.employees.createFailed'))
 }
 }
)

export const updateEmployee = createAsyncThunk(
 'employees/updateEmployee',
 async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
 try {
 const response = await employeesAPI.update(id, data)
 return response
 } catch (error: any) {
 const msg = error.response?.data?.message
 const errors = error.response?.data?.errors
 const errData = error.response?.data?.data
 if (errors) {
 const firstError = Object.values(errors)[0]
 return rejectWithValue(Array.isArray(firstError) ? firstError[0] : firstError)
 }
 if (errData) {
 const firstError = Object.values(errData)[0]
 return rejectWithValue(Array.isArray(firstError) ? firstError[0] : firstError)
 }
 return rejectWithValue(msg || i18n.t('errors.employees.updateFailed'))
 }
 }
)

export const updateEmployeePassword = createAsyncThunk(
 'employees/updateEmployeePassword',
 async (data: { employee_id: number; password: string }, { rejectWithValue }) => {
 try {
 const response = await employeesAPI.updatePassword(data)
 return response
 } catch (error: any) {
 const msg = error.response?.data?.message
 const errors = error.response?.data?.errors
 const errData = error.response?.data?.data
 if (errors) {
 const firstError = Object.values(errors)[0]
 return rejectWithValue(Array.isArray(firstError) ? firstError[0] : firstError)
 }
 if (errData) {
 const firstError = Object.values(errData)[0]
 return rejectWithValue(Array.isArray(firstError) ? firstError[0] : firstError)
 }
 return rejectWithValue(msg || i18n.t('errors.employees.passwordFailed'))
 }
 }
)

export const blockEmployee = createAsyncThunk(
 'employees/blockEmployee',
 async (id: number, { rejectWithValue }) => {
 try {
 const response = await employeesAPI.block(id)
 return { id, response }
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.employees.statusFailed'))
 }
 }
)

const employeesSlice = createSlice({
 name: 'employees',
 initialState,
 reducers: {
 clearEmployees: (state) => {
 state.data = []
 state.error = null
 state.pagination = null
 },
 clearCurrentEmployee: (state) => {
 state.currentEmployee = null
 state.currentLoading = false
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchEmployees.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchEmployees.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 const resultData = payload?.result ?? payload
 const items = resultData?.data ?? (Array.isArray(resultData) ? resultData : [])
 state.data = Array.isArray(items) ? items : []
 const meta = resultData?.meta
 if (meta) {
 state.pagination = {
 total: meta.total ?? 0,
 per_page: meta.per_page ?? 12,
 current_page: meta.current_page ?? 1,
 last_page: meta.last_page ?? 0,
 to: meta.to ?? 0,
 }
 }
 })
 .addCase(fetchEmployees.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(fetchEmployeeById.pending, (state) => {
 state.currentLoading = true
 })
 .addCase(fetchEmployeeById.fulfilled, (state, action) => {
 state.currentLoading = false
 state.currentEmployee = action.payload as Employee
 })
 .addCase(fetchEmployeeById.rejected, (state) => {
 state.currentLoading = false
 })
 .addCase(blockEmployee.fulfilled, (state, action) => {
 const { id } = action.payload
 const employee = state.data.find((e) => e.id === id)
 if (employee) {
 employee.is_active = employee.is_active ? 0 : 1
 }
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearEmployees, clearCurrentEmployee } = employeesSlice.actions
export default employeesSlice.reducer
